import { Link, useParams, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { BackButton, Badge, Button, Text } from "@andrum/ui";
import type { WellbeingCategory } from "@andrum/design-tokens";
import { useExperimentStore } from "@/stores/experiment-store";
import { hapticService } from "@/lib/capacitor";
import { canUsePremiumExperiment } from "@/lib/billing/premium-gate";
import { ExperimentScienceBlock } from "@/features/learn/components/SciencePanel";
import { useEffect, useMemo, useState } from "react";
import { durations } from "@/lib/motion/tokens";
import { buildPersonalWhy } from "@/features/experiments/lib/personal-why";
import { useOnboardingStore } from "@/stores/onboarding-store";
import { useCheckInStore } from "@/stores/check-in-store";
import { listRecentCheckIns } from "@/features/check-in/api/check-in-api";

function isCategory(value: string): value is WellbeingCategory {
  return (
    value === "connection" ||
    value === "movement" ||
    value === "recovery" ||
    value === "gratitude" ||
    value === "meaning" ||
    value === "presence"
  );
}

/** Drop filler endings that make the last step feel like empty cheerleading. */
function cleanInstruction(text: string): string {
  return text
    .replace(/\s*Klart\.?\s*$/i, "")
    .replace(/\s*Det räknas\.?\s*$/i, "")
    .replace(/\s*Done\.?\s*$/i, "")
    .trim();
}

type Path = "full" | "short";

/**
 * One linear flow:
 * - Overview: why + single primary "Börja" (starts assignment + enters steps)
 * - Steps: progress is only among real steps (1/N … N/N)
 * - "Jag är klar" only on the last step (or after short path)
 */
export function ExperimentDetailScreen() {
  const { t } = useTranslation();
  const { assignmentId } = useParams({ strict: false }) as {
    assignmentId: string;
  };
  const navigate = useNavigate();
  const reduce = useReducedMotion();
  const {
    getExperimentForAssignment,
    getTodayAssignment,
    startAssignment,
    skipAssignment,
    swapToday,
    assignments,
  } = useExperimentStore();
  const [premiumOk, setPremiumOk] = useState(true);
  const [busy, setBusy] = useState(false);
  /** null = overview; full/short = in progress */
  const [path, setPath] = useState<Path | null>(null);
  /** 0-based index within the active path (instructions or short=0 only) */
  const [stepIndex, setStepIndex] = useState(0);
  const goals = useOnboardingStore((s) => s.goals);
  const joyPathway = useOnboardingStore((s) => s.joyPathway);
  const calmPathway = useOnboardingStore((s) => s.calmPathway);
  const draft = useCheckInStore((s) => s.draft);
  const [lastCheckIn, setLastCheckIn] = useState<{
    mood: number | null;
    energy: number | null;
    stress: number | null;
  } | null>(null);

  useEffect(() => {
    void canUsePremiumExperiment().then(setPremiumOk);
  }, []);

  useEffect(() => {
    void listRecentCheckIns(1).then((rows) => {
      const row = rows[0];
      if (!row) return;
      setLastCheckIn({
        mood: row.mood ?? null,
        energy: row.energy,
        stress: row.stress,
      });
    });
  }, []);

  const assignment =
    assignments.find((a) => a.id === assignmentId) ?? getTodayAssignment();
  const experiment = assignment
    ? getExperimentForAssignment(assignment.id)
    : null;

  if (!assignment || !experiment) {
    return (
      <div className="flex h-dvh flex-col justify-center gap-4 overflow-hidden px-5">
        <Text variant="muted">{t("experiment.notFound")}</Text>
        <Button
          variant="soft"
          onClick={() => void navigate({ to: "/app/today" })}
        >
          {t("experiment.backToday")}
        </Button>
      </div>
    );
  }

  const locked = experiment.premium && !premiumOk;
  const instructions = experiment.instructions;
  const stepCount = path === "short" ? 1 : instructions.length;
  const safeIndex = Math.min(Math.max(0, stepIndex), Math.max(0, stepCount - 1));
  const onOverview = path === null;
  const onLastStep = path !== null && safeIndex >= stepCount - 1;
  const minMinutes = Math.max(
    2,
    experiment.minDurationMinutes ?? Math.min(2, experiment.durationMinutes)
  );

  const personal = useMemo(
    () =>
      buildPersonalWhy(experiment, {
        goals,
        joyPathway: joyPathway ?? null,
        calmPathway: calmPathway ?? null,
        mood: draft.mood ?? lastCheckIn?.mood ?? null,
        energy: draft.energy ?? lastCheckIn?.energy ?? null,
        stress: draft.stress ?? lastCheckIn?.stress ?? null,
        recommendationReason: assignment.recommendationReason,
      }),
    [
      experiment,
      goals,
      joyPathway,
      calmPathway,
      draft.mood,
      draft.energy,
      draft.stress,
      lastCheckIn,
      assignment.recommendationReason,
    ]
  );

  const ensureStarted = () => {
    if (assignment.status === "assigned") {
      void startAssignment(assignment.id);
    }
  };

  const beginFull = () => {
    if (locked) {
      void navigate({
        to: "/app/paywall",
        search: { reason: "experiment" },
      });
      return;
    }
    void hapticService.medium();
    ensureStarted();
    setPath("full");
    setStepIndex(0);
  };

  const beginShort = () => {
    if (locked) return;
    void hapticService.selection();
    ensureStarted();
    setPath("short");
    setStepIndex(0);
  };

  const goNext = () => {
    void hapticService.selection();
    setStepIndex((i) => Math.min(stepCount - 1, i + 1));
  };

  const goBack = () => {
    void hapticService.selection();
    if (safeIndex <= 0) {
      setPath(null);
      setStepIndex(0);
      return;
    }
    setStepIndex((i) => Math.max(0, i - 1));
  };

  const handleSkip = async () => {
    setBusy(true);
    void hapticService.selection();
    await skipAssignment(assignment.id);
    await swapToday();
    setBusy(false);
    void navigate({ to: "/app/today" });
  };

  const panelKey = onOverview
    ? "overview"
    : path === "short"
      ? "short"
      : `step-${safeIndex}`;

  return (
    <div className="flex h-dvh max-h-dvh flex-col overflow-hidden bg-background">
      <div className="shrink-0 px-5 pt-[calc(env(safe-area-inset-top)+12px)] pb-3">
        <BackButton
          className="mb-3"
          onClick={() => void navigate({ to: "/app/today" })}
        >
          {t("experiment.backToday")}
        </BackButton>
        <div className="mb-2 flex flex-wrap items-center gap-2">
          {isCategory(experiment.category) ? (
            <Badge category={experiment.category} />
          ) : null}
          {experiment.premium ? (
            <span className="rounded-full bg-primary-soft px-2.5 py-0.5 text-xs font-medium text-primary">
              {t("plus")}
            </span>
          ) : null}
        </div>
        <Text variant="title" as="h1" className="line-clamp-2 text-xl">
          {experiment.title}
        </Text>
        <Text variant="caption" className="mt-1">
          {t("experiment.difficulty", {
            minutes:
              path === "short" ? minMinutes : experiment.durationMinutes,
            level: experiment.difficulty,
          })}
        </Text>

        {/* One progress system: only real steps, never overview+steps mix */}
        {!onOverview && stepCount > 0 ? (
          <div className="mt-3 space-y-1.5">
            <div className="flex items-center justify-between gap-2">
              <Text variant="label" className="text-primary">
                {path === "short"
                  ? t("experiment.phaseShort")
                  : t("experiment.phaseStep", {
                      current: safeIndex + 1,
                      total: stepCount,
                    })}
              </Text>
              {path === "full" ? (
                <Text variant="caption" className="tabular-nums">
                  {safeIndex + 1}/{stepCount}
                </Text>
              ) : null}
            </div>
            {path === "full" ? (
              <div
                className="flex gap-1"
                role="progressbar"
                aria-valuenow={safeIndex + 1}
                aria-valuemin={1}
                aria-valuemax={stepCount}
              >
                {Array.from({ length: stepCount }, (_, i) => (
                  <div
                    key={i}
                    className={
                      i <= safeIndex
                        ? "h-1 flex-1 rounded-full bg-primary transition-colors duration-300 ease-out"
                        : "h-1 flex-1 rounded-full bg-surface-muted transition-colors duration-300 ease-out"
                    }
                  />
                ))}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="relative min-h-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-y-contain px-5 py-2 [-webkit-overflow-scrolling:touch]">
        <AnimatePresence mode="wait">
          <motion.div
            key={panelKey}
            initial={reduce ? false : { opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, y: -8 }}
            transition={{ duration: durations.normal }}
            className="flex min-h-full flex-col justify-center gap-4 py-1"
          >
            {onOverview ? (
              <>
                <div className="space-y-1">
                  <Text variant="heading" as="h2" className="text-lg">
                    {t("experiment.overviewTitle")}
                  </Text>
                  <Text variant="muted" className="text-sm leading-snug">
                    {personal.minutesLine}
                  </Text>
                </div>
                <ExperimentScienceBlock
                  slug={experiment.slug}
                  category={experiment.category}
                  rationale={personal.whyBody}
                  personalLine={personal.personalLine}
                />
                {locked ? (
                  <Text variant="body" className="text-sm leading-snug">
                    {t("experiment.premiumLocked")}
                  </Text>
                ) : null}
              </>
            ) : null}

            {path === "full" ? (
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary text-lg font-semibold text-primary-foreground">
                    {safeIndex + 1}
                  </span>
                  <div className="min-w-0 flex-1 space-y-2 pt-1">
                    <Text
                      variant="body"
                      className="text-base leading-relaxed text-foreground"
                    >
                      {cleanInstruction(instructions[safeIndex] ?? "")}
                    </Text>
                  </div>
                </div>
              </div>
            ) : null}

            {path === "short" ? (
              <div className="space-y-3">
                <Text variant="heading" as="h2" className="text-lg">
                  {t("experiment.shortOnTime")}
                </Text>
                <div className="rounded-2xl border border-secondary/20 bg-secondary-soft/40 p-4">
                  <Text variant="body" className="leading-relaxed">
                    {experiment.smallerVersion}
                  </Text>
                </div>
                <Text variant="caption" className="leading-snug text-foreground-muted">
                  {t("experiment.shortHint", { minutes: minMinutes })}
                </Text>
              </div>
            ) : null}
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="shrink-0 space-y-2 border-t border-border bg-surface-raised px-5 pt-3 pb-[calc(env(safe-area-inset-bottom)+16px)]">
        {onOverview ? (
          <>
            <Button
              fullWidth
              className="min-h-12"
              disabled={locked}
              onClick={beginFull}
            >
              {locked ? t("experiment.unlockPlus") : t("experiment.begin")}
            </Button>
            {!locked && experiment.smallerVersion ? (
              <button
                type="button"
                onClick={beginShort}
                className="w-full py-1.5 text-center text-[11px] leading-snug text-foreground-subtle/80 transition-colors hover:text-foreground-muted"
              >
                {t("experiment.shortPathSoft", { minutes: minMinutes })}
              </button>
            ) : null}
            <Button
              fullWidth
              variant="ghost"
              loading={busy}
              onClick={() => void handleSkip()}
            >
              {t("experiment.skipSwap")}
            </Button>
          </>
        ) : (
          <>
            <div className="flex gap-2">
              <Button
                fullWidth
                variant="ghost"
                className="min-h-11"
                onClick={goBack}
              >
                {safeIndex <= 0 ? t("experiment.backToStart") : t("back")}
              </Button>
              {!onLastStep ? (
                <Button
                  fullWidth
                  variant="soft"
                  className="min-h-11"
                  onClick={goNext}
                >
                  {t("experiment.nextStep")}
                </Button>
              ) : null}
            </div>
            {onLastStep ? (
              <Link
                to="/experiment/$assignmentId/reflect"
                params={{ assignmentId: assignment.id }}
                onClick={() => {
                  void hapticService.success();
                }}
                className="inline-flex min-h-12 w-full items-center justify-center rounded-2xl bg-secondary px-6 text-base font-medium text-secondary-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {t("experiment.imDone")}
              </Link>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
